/**
 * Image Compression Utility
 * Client-side image compression using Canvas API
 */

export interface CompressionOptions {
  maxWidth?: number;
  maxHeight?: number;
  quality?: number; // 0 to 1, default 0.8
  type?: string; // 'image/jpeg' | 'image/png' | 'image/webp'
}

export interface CompressionResult {
  file: File;
  originalSize: number;
  compressedSize: number;
  compressionRatio: number;
}

const DEFAULT_OPTIONS: CompressionOptions = {
  maxWidth: 1920,
  maxHeight: 1080,
  quality: 0.85,
  type: 'image/jpeg',
};

/**
 * Compress an image file
 * @param file - The image file to compress
 * @param options - Compression options
 * @returns Promise with compressed file and stats
 */
export async function compressImage(
  file: File,
  options: CompressionOptions = {}
): Promise<CompressionResult> {
  const opts = { ...DEFAULT_OPTIONS, ...options };

  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);

    img.onload = () => {
      URL.revokeObjectURL(url);

      // Calculate new dimensions maintaining aspect ratio
      let { width, height } = img;
      const { maxWidth, maxHeight } = opts;

      if (maxWidth && width > maxWidth) {
        height = (height * maxWidth) / width;
        width = maxWidth;
      }
      if (maxHeight && height > maxHeight) {
        width = (width * maxHeight) / height;
        height = maxHeight;
      }

      // Create canvas
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;

      const ctx = canvas.getContext('2d');
      if (!ctx) {
        reject(new Error('Failed to get canvas context'));
        return;
      }

      // Fill white background for JPEG (to avoid black background on transparent PNG)
      if (opts.type === 'image/jpeg') {
        ctx.fillStyle = '#FFFFFF';
        ctx.fillRect(0, 0, width, height);
      }

      // Draw image
      ctx.drawImage(img, 0, 0, width, height);

      // Convert to blob
      canvas.toBlob(
        (blob) => {
          if (!blob) {
            reject(new Error('Failed to create blob'));
            return;
          }

          // Create new file from blob
          const compressedFile = new File([blob], file.name, {
            type: opts.type || file.type,
            lastModified: Date.now(),
          });

          const originalSize = file.size;
          const compressedSize = compressedFile.size;
          const compressionRatio = ((originalSize - compressedSize) / originalSize) * 100;

          resolve({
            file: compressedFile,
            originalSize,
            compressedSize,
            compressionRatio,
          });
        },
        opts.type || file.type,
        opts.quality
      );
    };

    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Failed to load image'));
    };

    img.src = url;
  });
}

/**
 * Get recommended compression options based on image usage
 */
export function getCompressionPreset(
  preset: 'thumbnail' | 'banner' | 'content' | 'landing'
): CompressionOptions {
  switch (preset) {
    case 'thumbnail':
      return {
        maxWidth: 900,
        maxHeight: 675,
        quality: 0.82,
        type: 'image/webp',
      };
    case 'banner':
      return {
        maxWidth: 1920,
        maxHeight: 800,
        quality: 0.84,
        type: 'image/webp',
      };
    case 'landing':
      return {
        maxWidth: 1920,
        maxHeight: 1080,
        quality: 0.85,
        type: 'image/webp',
      };
    case 'content':
    default:
      return {
        maxWidth: 1200,
        maxHeight: 800,
        quality: 0.82,
        type: 'image/webp',
      };
  }
}

/**
 * Format file size to human readable string
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

/**
 * Check if file should be compressed (compress images > 150KB)
 */
export function shouldCompress(file: File, thresholdKB: number = 150): boolean {
  return file.size > thresholdKB * 1024 && file.type.startsWith('image/');
}

/**
 * Map known heavy external URLs (2MB - 8.4MB) to pre-compressed local WebP assets (<160KB)
 */
const OPTIMIZED_IMAGE_MAP: Record<string, string> = {
  'https://i.ibb.co/kvmbFMs/bn1.jpg': '/assets/optimized/banner-1.webp',
  'https://i.ibb.co/ksgLGSz2/nunnha.jpg': '/assets/optimized/banner-2.webp',
  'https://i.ibb.co/n8j6pjnX/bannner-ok.jpg': '/assets/optimized/banner-3.webp',
  'https://i.ibb.co/QFwxv3V4/644b47ed-9229-46e1-9aed-8f15ab13208a.jpg': '/assets/optimized/banner-08.webp',
  'https://i.ibb.co/W4SHyCB3/m-i.png': '/assets/optimized/svc-dan-dung.webp',
  'https://i.ibb.co/pr3r1t92/Gemini-Generated-Image-v1b9dv1b9dv1b9dv.png': '/assets/optimized/svc-thanh-xuan.webp',
  'https://i.ibb.co/bMQGd0YK/Gemini-Generated-Image-44au8r44au8r44au.png': '/assets/optimized/svc-hoang-mai.webp',
  'https://i.ibb.co/sv8pD2cc/Gemini-Generated-Image-juw167juw167juw1.png': '/assets/optimized/svc-ba-dinh.webp',
  'https://i.ibb.co/M5VyCZxf/Gemini-Generated-Image-79yp3c79yp3c79yp.png': '/assets/optimized/svc-hai-ba-trung.webp',
  'https://i.ibb.co/m5gkh6RJ/Gemini-Generated-Image-bc56bhbc56bhbc56.png': '/assets/optimized/svc-dong-da.webp',
  'https://i.ibb.co/SDWXgfBf/z6586686874990-e70da7f35138432d954e5cd375e3fb59.jpg': '/assets/optimized/svc-other-1.webp',
  'https://i.ibb.co/TBKSh8H3/C-nh-thi-c-ng-m-ng-c-c-t-i-H-N-i.png': '/assets/optimized/post-thi-cong-hn.webp',
  'https://i.ibb.co/jPnSxhRs/Khoan-d-n-p-c-c-d-n-n-n-m-ng-Vi-t-Nam.png': '/assets/optimized/post-khoan-dan.webp',
  'https://i.ibb.co/5W4xW6k3/L-M-SAO-KI-M-TRA-CH-T-L-NG-C-C-KHOAN-NH-I.png': '/assets/optimized/post-kiem-tra-cl.webp',
  'https://i.ibb.co/Kz6pxvTs/S-U-C-C-KHOAN-NH-I-NH-D-N-H-N-I-BAO-NHI-U-L.png': '/assets/optimized/post-do-sau-coc.webp',
  'https://i.ibb.co/6cpzg4L6/KHOAN-C-C-NH-I-C-G-Y-TI-NG-N-KH-NG.png': '/assets/optimized/post-tieng-on.webp',
  'https://raw.githubusercontent.com/dinhkhanhtung/khoancocnhoi-website/main/Anh_khoan_coc_nhoi/z6586686864376_5d6f4365575fca0b349b19cf7fd3940c.jpg': '/assets/optimized/home-main.webp',
  'https://raw.githubusercontent.com/dinhkhanhtung/khoancocnhoi-website/main/Anh_khoan_coc_nhoi/D800-1000-1200/z6586700092181_78cd2018f05bc3169ccaeae812007acb.jpg': '/assets/optimized/home-d800.webp',
  'https://raw.githubusercontent.com/dinhkhanhtung/khoancocnhoi-website/main/Anh_khoan_coc_nhoi/z6586686864706_d76eff60498e18ea3d60ca4791f397c0.jpg': '/assets/optimized/home-sub.webp',
  'https://raw.githubusercontent.com/dinhkhanhtung/khoancocnhoi-website/main/files/default/6746/untitled1_lYBQP8LE.png': '/assets/optimized/logo.webp',
};

export function optimizeImageUrl(url?: string): string {
  if (!url || typeof url !== 'string') return url || '';
  const trimmed = url.trim();
  return OPTIMIZED_IMAGE_MAP[trimmed] || trimmed;
}

export function cleanQuillHtml(html: string): string {
  if (!html || typeof html !== 'string') return '';
  return html
    .replace(/<(\/?)h1\b/gi, '<$1h2')
    .replace(/\u00a0/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/<span[^>]*class="[^"]*ql-cursor[^"]*"[^>]*>[\s\S]*?<\/span>/g, '')
    .replace(/<\/strong><strong>/g, '')
    .replace(/<\/em><em>/g, '')
    .replace(/<\/u><u>/g, '')
    .replace(/<\/s><s>/g, '')
    .replace(/<strong><\/strong>/g, '')
    .replace(/<em><\/em>/g, '')
    .replace(/<span[^>]*><\/span>/g, '')
    .replace(/\uFEFF/g, '');
}

export function optimizeDataImages<T>(obj: T): T {
  if (!obj) return obj;
  if (typeof obj === 'string') {
    let res = OPTIMIZED_IMAGE_MAP[obj.trim()] || obj;
    for (const [orig, opt] of Object.entries(OPTIMIZED_IMAGE_MAP)) {
      if (res.includes(orig)) {
        res = res.split(orig).join(opt);
      }
    }
    if (res.includes('&nbsp;') || res.includes('\u00a0') || res.includes('ql-cursor')) {
      res = cleanQuillHtml(res);
    }
    return res as unknown as T;
  }
  if (Array.isArray(obj)) {
    return obj.map(item => optimizeDataImages(item)) as unknown as T;
  }
  if (typeof obj === 'object') {
    if ('seconds' in (obj as any) || obj instanceof Date) return obj;
    const out: any = {};
    for (const k of Object.keys(obj)) {
      out[k] = optimizeDataImages((obj as any)[k]);
    }
    return out;
  }
  return obj;
}


